/**
 * Per-domain build config for the LogPulse marketing site.
 *
 * Each ccTLD is a separate Cloudflare Pages project that builds the same
 * source with VITE_SITE_LOCALE set. Marketing pages live at root paths on
 * every domain (no /nl/, /fr/, /de/ prefix). Docs and blog are EN-only and
 * served exclusively from logpulse.io — ccTLDs link to .io for those paths
 * and 301-redirect any incoming /docs or /blog requests via _redirects.
 */

export const LOCALES = ['en', 'nl', 'fr', 'de'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_ORIGINS: Record<Locale, string> = {
  en: 'https://logpulse.io',
  nl: 'https://logpulse.nl',
  de: 'https://logpulse.de',
  fr: 'https://logpulse.fr',
};

export const HREFLANG_TAGS: Record<Locale, string> = {
  en: 'en',
  nl: 'nl-NL',
  de: 'de-DE',
  fr: 'fr-FR',
};

export const OG_LOCALE_TAGS: Record<Locale, string> = {
  en: 'en_US',
  nl: 'nl_NL',
  de: 'de_DE',
  fr: 'fr_FR',
};

const RAW_LOCALE = (import.meta.env?.VITE_SITE_LOCALE ?? 'en') as string;
export const SITE_LOCALE: Locale = (LOCALES as readonly string[]).includes(RAW_LOCALE)
  ? (RAW_LOCALE as Locale)
  : 'en';

export const SITE_ORIGIN = LOCALE_ORIGINS[SITE_LOCALE];

/** Contact form endpoint — Cloudflare Worker that mails via Microsoft Graph.
 *  Override per-environment with VITE_CONTACT_API_URL; defaults to the
 *  production custom route on forms.logpulse.io. We keep marketing-form
 *  endpoints on a separate subdomain from the product API (api.logpulse.io)
 *  so they can scale independently and share form-specific CORS rules. */
export const CONTACT_API_URL: string =
  (import.meta.env?.VITE_CONTACT_API_URL as string | undefined) ?? 'https://forms.logpulse.io/contact';

/** Paths that only exist on the EN site (logpulse.io). Shared resources —
 *  no need to fragment docs, blog, or community across 4 ccTLDs. */
export function isEnOnlyPath(path: string): boolean {
  return (
    path === '/docs' || path.startsWith('/docs/') ||
    path === '/blog' || path.startsWith('/blog/') ||
    path === '/community' || path.startsWith('/community/')
  );
}

/** Marketing paths that get hreflang alternates and exist on every locale domain. */
export function isMarketingPath(path: string): boolean {
  if (path === '/') return true;
  if (isEnOnlyPath(path)) return false;
  const cleanPath = path.split('#')[0];
  // Top-level slug (e.g. /security, /compare)
  if (/^\/[a-z][a-z0-9-]*$/i.test(cleanPath)) return true;
  // /compare/<duo-slug>
  if (/^\/compare\/[a-z0-9-]+$/i.test(cleanPath)) return true;
  return false;
}

/**
 * Resolve an internal href to a navigable target for the current build.
 * - Same-domain marketing paths → unchanged (relative)
 * - Docs/blog paths on a non-EN build → absolute URL to logpulse.io
 * - External / hash-only / non-internal → unchanged
 */
export function resolveHref(href: string): string {
  if (!href.startsWith('/')) return href;
  if (SITE_LOCALE !== 'en' && isEnOnlyPath(href.split('#')[0])) {
    return `${LOCALE_ORIGINS.en}${href}`;
  }
  return href;
}

/** Absolute URL on a target locale's domain for the same path. */
export function urlOnLocale(path: string, locale: Locale): string {
  const cleanPath = path === '/' ? '' : path;
  return `${LOCALE_ORIGINS[locale]}${cleanPath}`;
}
